import DB from 'src/initializers/DB'
import * as Utils from 'src/initializers/Utils'
import Logger from 'src/initializers/Logger'
import airbrake from 'src/initializers/Airbrake'
import { InternalError, PublicError, NotWrappedError, UnhandledErrorInQueue } from 'src/Error'
import { NO_USER_AUTHENTICATED, INVALID_PARAMETER, OTHER } from 'src/ErrorCodes'

const NAME = window.PurchEaseLibraryName || 'PE'

// get/set deviceUuid in cookies
let deviceUuid = DB.get('PurchEase.DEVICE_UUID', 'cookie')
if (!deviceUuid) {
  deviceUuid = Utils.generateUUID()
  DB.set('PurchEase.DEVICE_UUID', deviceUuid, 'cookie')
} else if (deviceUuid instanceof Error) {
  deviceUuid = 'ERROR'
}

const Constants = {
  NAME,
  DEVICE_UUID: 'WEB/' + deviceUuid,
}

const Core = {
  /**
   * Used to store classes
   */
  Ressources: {},
  Controllers: {},

  /**
   * Used to store instances of class
   */
  controllers: {},
  ressources: {},
  promises: {},

  publicWrapper (options, callback) {
    // TODO: use types to remove this (verify at build step)
    if (!options.name) {
      airbrake.notify(new InternalError('missing _name_ in public method'))
    }
    // TODO: use types to remove this (verify at build step)
    ['onSuccess', 'onFailure', 'onComplete'].forEach((cb) => {
      if (!options.parameters.includes(cb)) {
        airbrake.notify(new InternalError(`missing callback ${cb} in public method`))
      }
    })

    const params = Utils.extractParamsFromArguments(options.arguments, options.parameters)
    const { onSuccess, onFailure, onComplete, ...paramsToPass } = params

    function newOnComplete () {
      // if (Core.get('PARTNER_NAME') === 'Bioderma') { Core.set('UPLOAD_LOCK', false) }
      if (onComplete && (typeof onComplete === 'function')) {
        Logger.warn(`(DeprecationWarning) using parameter "onComplete" is deprecated - use "promise.finally" instead`)
        onComplete()
      }
    }

    if (options.validations) {
      const errors = []
      // TODO: refactor without "try catch" (it was for the old API)
      options.validations.forEach((validation) => {
        if (!options.parameters.includes(validation.name)) {
          const message = `validation off parameter "${validation.name}" which is not in extracted parameters [${options.parameters}]`
          airbrake.notify(new InternalError(message))
        }
        try {
          this.validateParam(options.name, params[validation.name], validation)
        } catch (e) {
          errors.push(e)
        }
      })
      if (errors.length !== 0) {
        return this.promiseHandleError(onFailure, newOnComplete)(errors[0])
      }
    }

    let promise
    if (callback) {
      promise = callback(paramsToPass)
    } else {
      promise = this.execute('Api', options.name, paramsToPass)
    }
    return this.handlePromise(promise, { onSuccess, onFailure, onComplete: newOnComplete })
  },

  validateParam (publicMethodName, parameter, { undef, presence, equal, notEqual, nan, type, message, name }) {
    // TODO: use types to remove this (verify at build step)
    if (!publicMethodName) {
      airbrake.notify(new InternalError('missing _name_ in public method'))
    }
    if (typeof undef !== 'undefined' && undef !== (typeof parameter === 'undefined')) {
      const defaultMessage = `parameter "${name}" ${undef ? 'must be' : 'can not be'} undefined`
      this.throwInvalidParameter(message || defaultMessage, { parameter: name, reason: undef ? 'undefined' : 'defined' })
    }
    if (typeof presence !== 'undefined' && presence && typeof parameter === 'undefined') {
      const defaultMessage = `parameter "${name}" must be provided`
      this.throwInvalidParameter(message || defaultMessage, { parameter: name, reason: 'blank' })
    }
    if (typeof equal !== 'undefined' && parameter !== equal) {
      const defaultMessage = `parameter "${name}" must be equal to ${equal}`
      this.throwInvalidParameter(message || defaultMessage, { parameter: name, reason: 'not_equal' })
    }
    if (typeof notEqual !== 'undefined' && parameter === notEqual) {
      const defaultMessage = `parameter "${name}" must be different than ${notEqual}`
      this.throwInvalidParameter(message || defaultMessage, { parameter: name, reason: 'equal' })
    }
    if (typeof nan !== 'undefined' && nan !== isNaN(parameter)) {
      const defaultMessage = `parameter "${name}" ${NaN ? 'must be' : 'can not be'} NaN`
      this.throwInvalidParameter(message || defaultMessage, { parameter: name, reason: 'equal' })
    }
    if (type && typeof parameter !== 'undefined' && Utils.getType(parameter) !== type) {
      const defaultMessage = `parameter "${name}" must be ${type === 'array' ? 'an' : 'a'} ${type}`
      this.throwInvalidParameter(message || defaultMessage, { parameter: name, reason: 'wrong_type' })
    }
  },

  throwInvalidParameter (message, options) {
    throw new PublicError(INVALID_PARAMETER, message, options)
  },

  missingUserError () {
    return new PublicError(NO_USER_AUTHENTICATED, 'you must use "authorizeUserWithIdentifier" before "bulkUploadWithFileInput"')
  },

  promiseInQueueHandleError (error) {
    if (error instanceof PublicError) {
      Logger.error('UnhandledPromiseRejectionWarning: Unhandled promise rejection', error)
    } else {
      airbrake.notify({
        error: new UnhandledErrorInQueue(error),
        params: { module: 'Code', original: error },
      })
    }
  },

  promiseHandleSuccess (onSuccess, onComplete) {
    return (data) => {
      if (onComplete && (typeof onComplete === 'function')) { onComplete() }
      if (onSuccess && (typeof onSuccess === 'function')) {
        Logger.warn(`(DeprecationWarning) using parameter "onSuccess" is deprecated - use "promise.then" instead`)
        onSuccess(data)
        return Promise.resolve()
      }
      return Promise.resolve(data)
    }
  },

  promiseHandleError (onFailure, onComplete) {
    return (error) => {
      const exposedError = this.wrappedError(error)
      if (onComplete && (typeof onComplete === 'function')) { onComplete() }
      if (onFailure && (typeof onFailure === 'function')) {
        Logger.warn(`(DeprecationWarning) using parameter "onFailure" is deprecated - use "promise.catch" instead`)
        onFailure(exposedError)
        return Promise.resolve()
      }
      return Promise.reject(exposedError)
    }
  },

  handlePromise (promise, { onSuccess, onFailure, onComplete }) {
    return promise.then(
      this.promiseHandleSuccess(onSuccess, onComplete),
      this.promiseHandleError(onFailure, onComplete),
    )
  },

  wrappedError (error) {
    if (error instanceof PublicError) {
      return error
    } else {
      airbrake.notify({
        error: new NotWrappedError(error),
        params: { module: 'Core', original: error },
      })
      return new PublicError(OTHER, error.message)
    }
  },

  get (constant) {
    return Constants[constant]
  },
  set (constant, value) {
    Constants[constant] = value
  },

  getRessource (ressource) {
    if (typeof this.Ressources[ressource] === 'undefined') {
      console.error(Constants.NAME + ': ' + ressource + ' is not a valid ressource')
      return false
    }
    if (typeof this.ressources[ressource] === 'undefined') {
      switch (ressource) {
        case 'User':
          console.error(Constants.NAME + ': you must log a user before')
          break
        default:
          console.error(Constants.NAME + ': ressource ' + ressource + ' is not instanciated')
          break
      }
      return false
    }
    return this.ressources[ressource]
  },
  setRessource (ressource, args) {
    if (typeof this.Ressources[ressource] === 'undefined') {
      console.error(Constants.NAME + ': ' + ressource + ' is not a valid ressource')
      return false
    }
    this.ressources[ressource] = new this.Ressources[ressource](args)
    this.resolvePromiseForRessource(ressource, args)
    return this.ressources[ressource]
  },
  unsetRessource (ressource) {
    if (typeof this.Ressources[ressource] === 'undefined') {
      console.error(Constants.NAME + ': ' + ressource + ' is not a valid ressource')
      return false
    }
    delete this.ressources[ressource]
    this.rejectPromiseForRessource(ressource)
    return true
  },
  getPromiseForRessource (ressource) {
    if (typeof this.ressources[ressource] !== 'undefined') {
      return Promise.resolve(this.ressources[ressource])
    }
    if (typeof this.promises[ressource] === 'undefined') {
      this.promises[ressource] = new Promise((resolve, reject) => {
        this.promises[ressource + 'Resolver'] = resolve
        this.promises[ressource + 'Rejecter'] = reject
      })
    }
    return this.promises[ressource]
  },
  resolvePromiseForRessource (ressource, data) {
    if (typeof this.promises[ressource + 'Resolver'] === 'undefined') {
      this.getPromiseForRessource(ressource)
    }
    if (typeof this.promises[ressource + 'Resolver'] === 'function') {
      this.promises[ressource + 'Resolver'](data)
    }
  },
  rejectPromiseForRessource (ressource, data) {
    if (typeof this.promises[ressource + 'Rejecter'] === 'undefined') {
      this.getPromiseForRessource(ressource)
    }
    if (typeof this.promises[ressource + 'Rejecter'] === 'function') {
      this.promises[ressource + 'Rejecter'](data)
    }
  },

  initController (controller, args) {
    if (!this.Controllers[controller]) {
      console.error(Constants.NAME + ': ' + controller + ' is not a valid controller')
      return false
    }
    this.controllers[controller] = new this.Controllers[controller](args)
    return true
  },

  /**
   * Available in every controller, in order to call other controllers' methods.
   * @method execute
   * @param {String} controller The controller implementing the method we want call
   * @param {String} method The method we want to call
   * @param {Object} args The parameters to pass to the function
   * @returns {Promise} Result of the method we executing, wrapped in a Promise if it was not the case
   */
  execute (controller, method, args) {
    if (!this.Controllers[controller]) {
      console.error(Constants.NAME + ': ' + controller + ' is not a valid controller')
      return Promise.resolve()
    }
    if (!this.controllers[controller]) {
      console.error(Constants.NAME + ': controller ' + controller + ' is not instantiated')
      return Promise.resolve()
    }
    if (!this.controllers[controller][method]) {
      console.error(Constants.NAME + ': undefined method ' + method + ' for controller ' + controller)
      return Promise.resolve()
    }
    if (typeof this.controllers[controller][method] !== 'function') {
      console.error(Constants.NAME + ': ' + controller + '.' + method + ' is not a function')
      return Promise.resolve()
    }
    // could apply permissions per client
    if (controller === 'Api' && method === 'getHome') {
      console.error(Constants.NAME + ': You are not allowed to use ' + method)
      return Promise.resolve()
    }
    try {
      const result = this.controllers[controller][method](args)
      if (result instanceof Promise) {
        return result
      } else {
        return Promise.resolve(result)
      }
    } catch (e) {
      return Promise.reject(e)
    }
  },

  messageReceived (event) {
    // console.log('event', event)
    // origin of message must come from our domain
    const baseUrl = __API_BASE_URL__
    if (event.origin === baseUrl) {
      // messaged delivered must be an object
      if (typeof event.data === 'object') {
        this.execute(event.data.controller, event.data.method, event.data.params)
      } else {
        console.debug(Constants.NAME + ': message received is not an object')
      }
    } else {
      // console.debug(Constants.NAME + ': message received with wrong origin', event.origin, baseUrl)
    }
  },
}
window.addEventListener('message', Core.messageReceived.bind(Core), false)
export default Core

Core.Constants = Constants
Core.Controllers = {
  Api: require('./controllers/Api').default,
}
Core.Ressources = {
  User: require('./ressources/User').default,
  Event: require('./ressources/Event').default,
  Receipt: require('./ressources/Receipt').default,
}



// WEBPACK FOOTER //
// ./src/Core.js